import React, { useEffect, useState } from "react";

import { Grid, Box } from "@mui/material";
import { CircularProgress, Stack } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { IoWarningOutline } from "react-icons/io5";
import { decodeToken, isExpired } from "react-jwt";
import { useHistory } from "react-router-dom";

import { AppConfig, PUBLIC_ROUTES } from "../../constants";
import { useAppState } from "../../context/StateContext";
import { Company } from "../../models/company";
import { Project } from "../../models/project";
import { User } from "../../models/user";
import CompanyService from "../../services/company.service";
import UserCompanyService from "../../services/user.company.service";
import UserService from "../../services/user.service";
import { UserStatus } from "../../util/enums";
import { getUrlToRedirect } from "../../util/getUrlToRedirect";
import { Button } from "../Form";
import { Color } from "../Form/Button";
import Modal from "../Generic/Modal";
import { NotificationToast } from "../Generic/Toast/NotificationToast";
import { PublicFooter } from "../PublicFooter";
import useStyles from "./styles";

const linkExpired = require("../../assets/img/link-expired.svg");
const rafiki = require("../../assets/img/rafiki-user-invite.svg");

type DecodedProps = {
  userId: number;
  companyId: number;
  userCompanyId: number;
  projects: Project[];
};

const AcceptOrDeclineInvite = () => {
  const history = useHistory();

  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  // eslint-disable-next-line no-restricted-globals
  const parsed: { token?: string } = queryString.parse(location.search);

  const [inviteDeclined, setInviteDeclined] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<number>();
  const [company, setCompany] = useState<Company>();
  const [user, setUser] = useState<User>();
  const [userId, setUserId] = useState<number>();
  const [userCompanyId, setUserCompanyId] = useState<number>();
  const [newInvitationHasAlreadyBeenMade, setNewInvitationHasAlreadyBeenMade] =
    useState<boolean>(false);
  const [token, setToken] = useState<string>("");
  const [isInvalidToken, setIsInvalidToken] = useState<boolean>(false);
  const [isExpiredToken, setIsExpiredToken] = useState<boolean>(false);
  const [isRevoked, setIsRevoked] = useState<boolean>(false);

  const classes = useStyles({
    isExpiredOrInvalidOrRejected: inviteDeclined || isInvalidToken || isExpiredToken,
    isRevoked,
  });

  useQuery(["company.get"], () => CompanyService.getById(companyId || 0), {
    enabled: !!companyId,
    onSuccess: (response) => {
      if (response && response.data && response.data.id) {
        setCompany(response.data);
      }
    },
  });

  useQuery(["user.get"], () => UserService.getById(userId || 0), {
    enabled: !!userId,
    onSuccess: (response) => {
      if (response && response.data && response.data.id) {
        setUser(response.data);
      }
    },
  });

  const { isFetching: isLoadingUserCompanyId } = useQuery(
    ["user.company.get"],
    () => UserCompanyService.getById(userCompanyId || 0),
    {
      enabled: !!userCompanyId,
      onSuccess: (response) => {
        if (!response || !response?.data || !response?.data?.id) {
          NotificationToast.warning(translate("userHasNotBeenInvited"), {
            id: "userHasNotBeenInvited",
          });

          history.replace("/");
        }

        const { status } = response.data;

        if (!status) {
          NotificationToast.warning(translate("userHasNotBeenInvited"), {
            id: "userHasNotBeenInvited",
          });

          history.replace("/");

          return;
        }

        if (status === UserStatus.ACTIVE || status === UserStatus.INACTIVE) {
          NotificationToast.warning(translate("youHaveAlreadyAcceptedThisInvitation"), {
            id: "youHaveAlreadyAcceptedThisInvitation",
          });

          history.replace("/");
        } else if (status === UserStatus.REJECTED) {
          setInviteDeclined(true);
        } else if (status === UserStatus.REVOKED) {
          setIsRevoked(true);
        }
      },
    }
  );

  const { mutate: userAcceptInvite, isLoading: isLoadingUserAcceptInvite } = useMutation(
    ["company.user.accept.invite"],
    ({ userId, companyId }: { userId: number; companyId: number }) =>
      CompanyService.userAcceptInvite(userId, companyId),
    {
      onSuccess: () => {
        history.replace("/");
      },
    }
  );

  const { mutate: userRejectInvite, isLoading: isLoadingUserRejectInvite } = useMutation(
    ["company.user.reject.invite"],
    ({ userId, companyId }: { userId: number; companyId: number }) =>
      CompanyService.userRejectInvite(userId, companyId),
    {
      onSuccess: () => {
        setInviteDeclined(true);
      },
    }
  );

  const { mutate: inviteUser, isLoading: isLoadingInviteUser } = useMutation(
    ["company.invite.user"],
    ({ userEmail, companyId }: { userEmail: string; companyId: number }) =>
      CompanyService.inviteUserInCompany(userEmail, companyId),
    {
      onSuccess: () => {
        const messageSuccess = translate("anEmailWasSentToUsernameWithYourRequest").replace(
          "*username*",
          user?.name || ""
        );

        NotificationToast.success(messageSuccess);
        history.replace("/");
      },
      onError: () => {
        setNewInvitationHasAlreadyBeenMade(false);
      },
    }
  );

  const { mutate: resendInviteUser, isLoading: isLoadingResendInviteUser } = useMutation(
    ["company.resend.invite.user"],
    ({ userId, companyId }: { userId: number; companyId: number }) =>
      CompanyService.userResendInvite(userId, companyId),
    {
      onSuccess: () => {
        const messageSuccess = translate("anEmailWasSentToUsernameWithYourRequest").replace(
          "*username*",
          user?.name || ""
        );

        NotificationToast.success(messageSuccess);
        history.replace("/");
      },
    }
  );

  const handleAcceptInvite = () => {
    if (userId && companyId) userAcceptInvite({ userId, companyId });
  };

  const handleRejectInvite = () => {
    if (userId && companyId) userRejectInvite({ userId, companyId });
  };

  const handleInviteUserAgain = () => {
    if (!isLoadingInviteUser && !newInvitationHasAlreadyBeenMade && user?.email && companyId) {
      setNewInvitationHasAlreadyBeenMade(true);

      inviteUser({ userEmail: user?.email, companyId });
    }
  };

  const handleResendInvite = () => {
    if (userId && companyId) resendInviteUser({ userId, companyId });
  };

  const getTitle = (): string => {
    if (inviteDeclined) {
      return translate("inviteDeclined");
    }

    if (isInvalidToken) {
      return `${translate("sorryTheLinkIsBroken")} :(`;
    }

    if (isExpiredToken) {
      return translate("sorryTheLinkHasExpired");
    }

    return translate("companyInvitation");
  };

  const renderDescription = (): JSX.Element => {
    const dataCy = "description";

    if (isInvalidToken) {
      return (
        <p className={classes.invitationDescriptionExpired} data-cy={dataCy}>
          {translate("theInviteLinkIsBrokenYouCanRequestANewOneByClickingOnTheButtonBelow")}
        </p>
      );
    }

    if (isExpiredToken) {
      return (
        <p className={classes.invitationDescriptionExpired} data-cy={dataCy}>
          {translate("theInviteLinkHasExpiredYouCanRequestANewOneByClickingOnTheButtonBelow")}
        </p>
      );
    }

    return (
      <p className={classes.invitationDescription} data-cy={dataCy}>
        {translate(
          inviteDeclined ? "weWillNotifyTheAdministratorFor" : "youHaveBeenInvitedToBePartOfCompany"
        )}{" "}
        <span>{`${company?.name || ""}.`}</span>{" "}
        {translate(
          inviteDeclined
            ? "youCanCloseThisWindow"
            : "youCanAcceptOrDeclineTheInvitationAndWeWillNotifyTheCompanyAdministrator"
        )}
      </p>
    );
  };

  const renderActions = (): JSX.Element => {
    if (isExpiredToken) {
      return (
        <Button
          onClick={handleResendInvite}
          loading={isLoadingResendInviteUser}
          disabled={isLoadingResendInviteUser}
          customColor={Color.SECONDARY}
          className={classes.actionButton}
          data-cy="resend-button"
        >
          {translate("resendInviteLink")}
        </Button>
      );
    }

    if (isInvalidToken) {
      return (
        <Button
          onClick={() => {
            history.replace("/");
          }}
          customColor={Color.SECONDARY}
          className={classes.actionButton}
          data-cy="go-back-button"
        >
          {translate("goBack")}
        </Button>
      );
    }

    if (inviteDeclined) {
      return (
        <p className={classes.invitationDescriptionDeclined} data-cy="description">
          {translate("ifYouRejectedThisInvitationByMistakePleaseRequestANewInvite")}{" "}
          <a href="#" onClick={handleInviteUserAgain} data-cy="invite-user-again">{`${translate(
            "here"
          )}.`}</a>
        </p>
      );
    }

    return (
      <>
        <Button
          onClick={handleRejectInvite}
          fullWidth
          loading={isLoadingUserRejectInvite}
          disabled={isLoadingUserAcceptInvite || isLoadingUserRejectInvite}
          customColor={Color.ERROR}
          className={classes.actionButton}
          data-cy="decline-button"
        >
          {translate("decline")}
        </Button>

        <Button
          onClick={handleAcceptInvite}
          fullWidth
          disabled={isLoadingUserAcceptInvite || isLoadingUserRejectInvite}
          loading={isLoadingUserAcceptInvite}
          customColor={Color.SUCCESS}
          className={classes.actionButton}
          data-cy="accept-button"
        >
          {translate("accept")}
        </Button>
      </>
    );
  };

  const renderRightSideContent = () => {
    if (!isLoadingUserCompanyId && !isRevoked) {
      return (
        <Grid item xs={12} sm={6}>
          <Box
            className={classes.asideRight}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box className={classes.containerAsideRight}>
              <Stack
                className={classes.sectionInvitation}
                justifyContent="center"
                alignItems="flex-start"
                spacing={1}
              >
                <h2 className={classes.title} data-cy="title">
                  {getTitle()}
                </h2>

                <Stack width="100%" justifyContent="center" alignItems="flex-start" spacing={3}>
                  {renderDescription()}
                </Stack>

                <Stack
                  width="100%"
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                  {renderActions()}
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Grid>
      );
    }
  };

  const renderImageLeft = () => {
    if (!isLoadingUserCompanyId && !isRevoked) {
      return (
        <div className={classes.containerRafiki}>
          <div className={classes.sectionRafiki}>
            <img
              src={isInvalidToken || isExpiredToken ? linkExpired : rafiki}
              className={classes.rafiki}
              data-cy={isInvalidToken || isExpiredToken ? "link-expired" : "rafiki-invite"}
            />
          </div>
        </div>
      );
    }
  };

  const renderContentLeft = () => {
    if (isRevoked && !isLoadingUserCompanyId) {
      return (
        <Stack alignItems="center" justifyContent="center" className={classes.revokeContainer}>
          <Stack
            justifyContent="center"
            alignItems="center"
            className={classes.revokeContent}
            spacing={1}
            direction="column"
          >
            <div className={classes.containerIcon}>
              <IoWarningOutline color="#F04438" size={32} />
            </div>
            <span className={classes.revokeTitle}>{translate("thisInviteWasRevoked")}</span>
            <span className={classes.revokeSubTitle}>
              {translate("oopsItLooksLikeThisInvitationHasBeenRevoked")}
            </span>
            <br />
            <span className={classes.revokeSubTitle}>
              {translate("ifYouThinkThisIsAnErrorPleaseContactTheCompanyResponsibleToBetter")}
            </span>
          </Stack>
        </Stack>
      );
    }

    return null;
  };

  useEffect(() => {
    if (parsed?.token) {
      setToken(parsed.token);
    } else {
      history.replace("/");
    }

    if (!currentUser) {
      return history.replace(`${PUBLIC_ROUTES.SIGN_IN}${getUrlToRedirect()}`);
    }
  }, [parsed]);

  useEffect(() => {
    if (token) {
      const decodedToken = decodeToken<DecodedProps>(token);
      const tokenIsExpired = isExpired(token);

      if (!currentUser) {
        return history.replace(`${PUBLIC_ROUTES.SIGN_IN}${getUrlToRedirect()}`);
      }

      if (!decodedToken?.companyId || !decodedToken?.userId || !decodedToken?.userCompanyId) {
        setIsInvalidToken(true);

        return;
      }

      if (decodedToken?.userId !== currentUser?.id) {
        return history.replace(`${PUBLIC_ROUTES.SIGN_IN}${getUrlToRedirect()}`);
      }

      if (tokenIsExpired) {
        setIsExpiredToken(true);
      }

      setCompanyId(decodedToken.companyId);

      setUserId(decodedToken.userId);

      setUserCompanyId(decodedToken.userCompanyId);
    }
  }, [token]);

  return (
    <div className={classes.container}>
      <Grid className={classes.containerGrid} container direction="row">
        <Grid item xs={12} sm={isLoadingUserCompanyId || isRevoked ? 12 : 6}>
          <Box className={classes.asideLeft}>
            <div className={classes.sectionAsideLeft}>
              <img
                src={AppConfig.appLogo}
                alt="session-logo"
                data-cy="sessionLogo"
                className={classNames("img-fluid", classes.shipeeziLogo)}
              />

              {renderImageLeft()}
            </div>

            {renderContentLeft()}

            {isLoadingUserCompanyId && (
              <div className={classes.loadingScreen} data-cy="loading">
                <CircularProgress size={75} />

                <span className={classes.loadingScreenLabel}>{translate("loadingInvitation")}</span>
              </div>
            )}
          </Box>
        </Grid>

        {renderRightSideContent()}
      </Grid>

      <Modal
        open={isLoadingInviteUser}
        showActionButtons={false}
        className={classes.loadingRequestNewInviteModal}
      >
        <section className={classes.loadingRequestNewInviteModalContent}>
          <h1 data-cy="title-request-new-invite">{translate("requestingNewInvite")}</h1>

          <CircularProgress size={60} />
        </section>
      </Modal>

      <PublicFooter />
    </div>
  );
};

export default AcceptOrDeclineInvite;
